import { logError, type Maybe, createShortUid } from '@amazon/vinyl-util'
import type { PlaylistEntry } from '@/playback/PlaylistController'
import type { PodcastEpisodeFields } from '@/podcast/podcastEpisodeFieldsFrag'
import { getLargestImage } from '@/util/imageUtil'
import { ctx } from '@/util/context'

const { target } = ctx('podcastEpisodeGqlUtil')

/**
 * Convert a `PodcastEpisode` GraphQL node into a `PlaylistEntry` so it can be
 * loaded by the player. Uses the streaming `audioUrl` returned by the
 * podcast service.
 */
export function podcastEpisodeToPlaylistEntry(
    episode: Maybe<PodcastEpisodeFields>
): PlaylistEntry | null {
    if (!episode) return null
    const audioUrl = episode.playbackInformation?.audioUrl
    if (!audioUrl) {
        logError(target, 'episode missing audioUrl', episode.id)
        return null
    }
    const image = getLargestImage(episode.images, 'PRIMARY')
    return {
        id: episode.id ?? createShortUid(),
        entity: episode,
        title: episode.title ?? '',
        albumTitle: episode.podcast?.title ?? '',
        artistName: episode.author ?? '',
        // The transport's "album" line on a podcast episode points at
        // the show detail page. The episode's own detail route is the
        // "title" target.
        trackId: episode.id ?? null,
        albumId: episode.podcast?.id ?? null,
        duration: episode.duration ?? 0,
        image,
        trackLoadOptions: {
            type: 'src',
            uri: audioUrl,
        },
    }
}
